/** Status → colour (§3.4) and audit action → badge colour (§12.3), as data so every screen
 * and test agrees. Unknown values fall back to neutral instead of breaking. */

export type Tone = "success" | "warning" | "orange" | "danger" | "neutral" | "primary" | "indigo" | "sky";

const STATUS: Record<string, Tone> = {
  PAID: "success",
  COMPLETED: "success",
  ACTIVE: "success",
  OPEN: "success",
  SUCCESS: "success",
  PENDING: "warning",
  PARTIALLY_REFUNDED: "warning",
  TRIAL: "warning",
  LOW_STOCK: "warning",
  PAST_DUE: "warning",
  PENDING_APPROVAL: "warning",
  ON_CREDIT: "orange",
  FAILED: "danger",
  OUT_OF_STOCK: "danger",
  SUSPENDED: "danger",
  REJECTED: "danger",
  VOIDED: "neutral",
  CLOSED: "neutral",
  CANCELLED: "neutral",
  INACTIVE: "neutral",
  ARCHIVED: "neutral",
};

const norm = (v: string) => v.trim().toUpperCase().replace(/[\s-]+/g, "_");

export function statusTone(value: string | null | undefined): Tone {
  if (!value) return "neutral";
  return STATUS[norm(value)] ?? "neutral";
}

/** "PARTIALLY_REFUNDED" → "PARTIALLY REFUNDED", "ON_CREDIT" → "ON CREDIT". */
export function statusLabel(value: string | null | undefined): string {
  return value ? norm(value).replace(/_/g, " ") : "—";
}

export type Role = "CASHIER" | "MANAGER" | "ADMIN" | "SUPERADMIN";
export const ROLE_TONE: Record<Role, Tone> = { SUPERADMIN: "indigo", ADMIN: "primary", MANAGER: "warning", CASHIER: "neutral" };
export const ROLE_LABEL: Record<Role, string> = { SUPERADMIN: "Superadmin", ADMIN: "Admin", MANAGER: "Manager", CASHIER: "Cashier" };

const AUDIT: Record<string, Tone> = {
  USER_CREATED: "success",
  PRODUCT_CREATED: "success",
  TENANT_CREATED: "success",
  CREDIT_REPAYMENT: "success",
  LOGIN_FAILED: "danger",
  USER_DEACTIVATED: "danger",
  PRODUCT_DEACTIVATED: "danger",
  REFUND_PROCESSED: "danger",
  PRODUCT_PRICE_CHANGED: "warning",
  CREDIT_LIMIT_SET: "warning",
  PRICE_OVERRIDE: "warning",
  CART_DISCOUNT_APPLIED: "warning",
  OVERRIDE_AUTHORIZED: "warning",
  USER_UPDATED: "warning",
  USER_PASSWORD_RESET: "warning",
  USER_PERMISSIONS_UPDATED: "warning",
  CREDIT_SALE: "orange",
  LOGIN: "neutral",
  TILL_OPENED: "neutral",
  TILL_CLOSED: "neutral",
  TILL_CASH_IN: "neutral",
  TILL_CASH_OUT: "neutral",
  PASSWORD_CHANGED: "neutral",
};

export function auditTone(action: string | null | undefined): Tone {
  return action ? (AUDIT[norm(action)] ?? "neutral") : "neutral";
}

/** Tailwind classes for a tone's pill: tint background + AA text shade of the same hue. */
export const TONE_PILL: Record<Tone, string> = {
  success: "bg-success-tint text-success-ink",
  warning: "bg-warning-tint text-warning",
  orange: "bg-orange-tint text-orange",
  danger: "bg-danger-tint text-danger-ink",
  neutral: "bg-neutral-200 text-neutral-700",
  primary: "bg-primary-tint text-primary-text",
  indigo: "bg-indigo-tint text-indigo",
  sky: "bg-sky-tint text-sky",
};

/** Icon chip (tinted square) classes per tone. */
export const TONE_CHIP: Record<Tone, string> = {
  success: "bg-success-tint text-success",
  warning: "bg-warning-tint text-warning",
  orange: "bg-orange-tint text-orange",
  danger: "bg-danger-tint text-danger",
  neutral: "bg-neutral-100 text-neutral-500",
  primary: "bg-primary-tint text-primary",
  indigo: "bg-indigo-tint text-indigo",
  sky: "bg-sky-tint text-sky",
};
